<template>
  <div class="sales-dashboard min-h-screen">
    <LoadingSpinner :isLoading="isLoading" />
    
    <!-- Header Section -->
    <div class="sd-header">
      <div class="w-full px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="sd-header__logo">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <div>
              <h1 class="text-xl font-semibold text-secondary-800">{{ $t('page_titles.agreement') }}</h1>
              <p class="text-sm text-gray-500">{{ $t('agreements.manage_contracts') || 'Hantera kundavtal' }}</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <!-- Skapa avtal (admin only) -->
            <button
              v-if="user.role === 'admin'"
              class="sd-btn sd-btn--success"
              @click.prevent="$modal.show('create-temporary-agreement')"
            >
              <BaseIcon icon="plus" class="mr-1" />
              {{ $t('agreements.create_temporary') }}
            </button>
            <div 
              v-if="totalWarnings > 0 && (this.user.role === 'admin' || this.user.role === 'seller')"
              class="relative inline-block"
              @mouseenter="showWarningTooltip = true"
              @mouseleave="showWarningTooltip = false"
            >
              <button 
                class="sd-btn sd-btn--warning"
                @click.prevent="showWarningsModal"
              >
                <BaseIcon icon="exclamation-triangle" class="mr-1" />
                {{ totalWarnings }}
              </button>
              <!-- Hover tooltip with warning breakdown -->
              <div 
                v-show="showWarningTooltip"
                class="absolute left-0 top-full mt-2 w-72 bg-white rounded-lg shadow-lg border border-secondary-200 p-4"
                style="z-index: 9999;"
              >
                <div class="text-sm font-semibold text-secondary-700 border-b border-secondary-200 pb-2 mb-2">
                  {{ $t('agreements.warnings_overview') }}
                </div>
                <ul class="space-y-1.5 text-sm">
                  <li v-if="agreementsWithoutAlarmsCount > 0" class="flex justify-between items-center">
                    <span class="text-secondary-600">{{ $t('agreements.warnings.filter_short.without_alarms') }}</span>
                    <span class="font-medium text-amber-600">{{ agreementsWithoutAlarmsCount }}</span>
                  </li>
                  <li v-if="invoiceWithoutPdfCount > 0" class="flex justify-between items-center">
                    <span class="text-secondary-600">{{ $t('agreements.warnings.filter_short.invoice_without_pdf') }}</span>
                    <span class="font-medium text-amber-600">{{ invoiceWithoutPdfCount }}</span>
                  </li>
                  <li v-if="extendedAgreementsCount > 0" class="flex justify-between items-center">
                    <span class="text-secondary-600">{{ $t('agreements.warnings.filter_short.extended') }}</span>
                    <span class="font-medium text-amber-600">{{ extendedAgreementsCount }}</span>
                  </li>
                  <li v-if="dismissedAgreementsPastExpDate > 0" class="flex justify-between items-center">
                    <span class="text-secondary-600">{{ $t('agreements.warnings.filter_short.dismissed_past_cancellation_date') }}</span>
                    <span class="font-medium text-amber-600">{{ dismissedAgreementsPastExpDate }}</span>
                  </li>
                  <li v-if="dismissedWithDevices > 0" class="flex justify-between items-center">
                    <span class="text-secondary-600">{{ $t('agreements.warnings.filter_short.dismissed_with_devices') }}</span>
                    <span class="font-medium text-amber-600">{{ dismissedWithDevices }}</span>
                  </li>
                </ul>
                <div class="mt-2 pt-2 border-t border-secondary-200 text-xs text-secondary-500">
                  {{ $t('agreements.click_to_view_details') }}
                </div>
              </div>
            </div>
            <button v-if="this.user.role === 'admin' || this.user.role === 'seller'" class="sd-btn sd-btn--secondary" @click.prevent="showFilterModal">
              <BaseIcon icon="filter" class="mr-1" />
              {{ $t("agreements.filters") }}
            </button>
            <button v-if="this.user.role === 'admin' || this.user.role === 'seller'" class="sd-btn sd-btn--primary" @click.prevent="exportXLS()">
              <BaseIcon icon="file-excel" class="mr-1" />
              {{ $t("export") }}
            </button>
            <button v-if="this.user.role === 'admin'" class="sd-btn sd-btn--secondary relative" @click.prevent="showMultipleAgreementModal">
              <BaseIcon icon="list" class="mr-1" />
              {{ $t("agreements.mark_multiple") }}
              <span
                v-if="selectedAgreementIds.length > 0"
                class="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
              >
                {{ selectedAgreementIds.length }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="w-full py-6">
      <!-- KPI Cards Section -->
      <div v-if="this.user.role === 'admin' || this.user.role === 'seller'" class="sd-card p-6 mb-6">
        <div class="flex items-center gap-2 mb-4">
          <svg class="w-5 h-5 text-securdia-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <h3 class="text-lg font-semibold tracking-tight text-secondary-800">{{ $t('agreements.expiring_contracts') }}</h3>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button 
            @click="toggleFilter(6)" 
            :class="[
              'bg-white rounded-lg p-3 text-center cursor-pointer transition-all duration-200',
              'border-l-4 border-red-500',
              activeFilter === 6 ? 'shadow-lg bg-red-50' : 'shadow-sm hover:shadow-md'
            ]"
          >
            <p class="text-2xl font-bold text-gray-800 mb-1">{{ getExpiringCount(6) }}</p>
            <span class="text-sm text-gray-600">{{ $t('agreements.expires_within') }} {{ $t('agreements.months_6') }}</span>
          </button>
          <button 
            @click="toggleFilter(12)" 
            :class="[
              'bg-white rounded-lg p-3 text-center cursor-pointer transition-all duration-200',
              'border-l-4 border-accent-500',
              activeFilter === 12 ? 'shadow-lg bg-accent-50' : 'shadow-sm hover:shadow-md'
            ]"
          >
            <p class="text-2xl font-bold text-gray-800 mb-1">{{ getExpiringCount(12) }}</p>
            <span class="text-sm text-gray-600">{{ $t('agreements.expires_within') }} {{ $t('agreements.months_12') }}</span>
          </button>
          <button 
            @click="toggleFilter(24)" 
            :class="[
              'bg-white rounded-lg p-3 text-center cursor-pointer transition-all duration-200',
              'border-l-4 border-gray-400',
              activeFilter === 24 ? 'shadow-lg bg-gray-100' : 'shadow-sm hover:shadow-md'
            ]"
          >
            <p class="text-2xl font-bold text-gray-800 mb-1">{{ getExpiringCount(24) }}</p>
            <span class="text-sm text-gray-600">{{ $t('agreements.expires_within') }} {{ $t('agreements.months_24') }}</span>
          </button>
          <button 
            @click="toggleStatus('active')"
            class="bg-white rounded-lg p-3 text-center border-l-4 border-blue-500 shadow-sm hover:shadow-md transition-all duration-200"
          >
            <p class="text-2xl font-bold text-gray-800 mb-1">{{ getActiveCount() }}</p>
            <span class="text-sm text-gray-600">{{ $t('agreements.total_active') }} {{ $t('agreements.contracts') }}</span>
            <span v-if="activeCategoryFilterLabel" class="block text-xs text-gray-500 mt-1">{{ activeCategoryFilterLabel }}</span>
          </button>
        </div>
        
        <!-- Agreement Status Filter Buttons -->
        <div class="mt-6">
          <div class="flex flex-wrap gap-2 items-center">
            <button 
              @click="toggleStatus(null)" 
              :class="[
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 border',
                activeStatusFilter === null 
                  ? 'shadow-lg bg-securdia-blue text-white border-securdia-blue' 
                  : 'bg-white text-gray-800 border-gray-300 shadow-sm hover:bg-gray-100'
              ]"
            >
              <span class="font-bold">{{ getTotalCount() }}</span>
              <span>{{ $t('agreements.all') }}</span>
            </button>
            <button 
              @click="toggleStatus('pending')" 
              :class="[
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 border',
                activeStatusFilter === 'pending' 
                  ? 'shadow-lg bg-securdia-blue text-white border-securdia-blue' 
                  : 'bg-white text-gray-800 border-gray-300 shadow-sm hover:bg-gray-100'
              ]"
            >
              <span class="font-bold">{{ getStatusCount('pending') }}</span>
              <span>{{ $t('agreements.pending') }}</span>
            </button>
            <button 
              @click="toggleStatus('active')" 
              :class="[
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 border',
                activeStatusFilter === 'active' 
                  ? 'shadow-lg bg-securdia-blue text-white border-securdia-blue' 
                  : 'bg-white text-gray-800 border-gray-300 shadow-sm hover:bg-gray-100'
              ]"
            >
              <span class="font-bold">{{ getStatusCount('active') }}</span>
              <span>{{ $t('agreements.active') }}</span>
            </button>
            <button 
              @click="toggleStatus('extended')" 
              :class="[
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 border',
                activeStatusFilter === 'extended' 
                  ? 'shadow-lg bg-securdia-blue text-white border-securdia-blue' 
                  : 'bg-white text-gray-800 border-gray-300 shadow-sm hover:bg-gray-100'
              ]"
            >
              <span class="font-bold">{{ getStatusCount('extended') }}</span>
              <span>{{ $t('agreements.extended') }}</span>
            </button>
            <button 
              @click="toggleStatus('redrawn')" 
              :class="[
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 border',
                activeStatusFilter === 'redrawn' 
                  ? 'shadow-lg bg-securdia-blue text-white border-securdia-blue' 
                  : 'bg-white text-gray-800 border-gray-300 shadow-sm hover:bg-gray-100'
              ]"
            >
              <span class="font-bold">{{ getStatusCount('redrawn') }}</span>
              <span>{{ $t('agreements.redrawn') }}</span>
            </button>
            <button 
              @click="toggleStatus('dismissed')" 
              :class="[
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 border',
                activeStatusFilter === 'dismissed' 
                  ? 'shadow-lg bg-securdia-blue text-white border-securdia-blue' 
                  : 'bg-white text-gray-800 border-gray-300 shadow-sm hover:bg-gray-100'
              ]"
            >
              <span class="font-bold">{{ getStatusCount('dismissed') }}</span>
              <span>{{ $t('agreements.dismissed') }}</span>
            </button>
            <button 
              @click="toggleStatus('returned')" 
              :class="[
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 border',
                activeStatusFilter === 'returned' 
                  ? 'shadow-lg bg-securdia-blue text-white border-securdia-blue' 
                  : 'bg-white text-gray-800 border-gray-300 shadow-sm hover:bg-gray-100'
              ]"
            >
              <span class="font-bold">{{ getStatusCount('returned') }}</span>
              <span>{{ $t('agreements.returned') }}</span>
            </button>
            <button
              @click="toggleStatus('no_status')"
              :class="[
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 border',
                activeStatusFilter === 'no_status'
                  ? 'shadow-lg bg-securdia-blue text-white border-securdia-blue'
                  : 'bg-white text-gray-800 border-gray-300 shadow-sm hover:bg-gray-100'
              ]"
            >
              <span class="font-bold">{{ getStatusCount('no_status') }}</span>
              <span>{{ $t('agreements.no_status') }}</span>
            </button>
            <button
              @click="toggleStatus('active_old')"
              :class="[
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 border',
                activeStatusFilter === 'active_old' 
                  ? 'shadow-lg bg-securdia-blue text-white border-securdia-blue' 
                  : 'bg-white text-gray-800 border-gray-300 shadow-sm hover:bg-gray-100'
              ]"
            >
              <span class="font-bold">{{ getStatusCount('active_old') }}</span>
              <span>{{ $t('agreements.active_old') }}</span>
            </button>
            <button
              v-if="user.role === 'admin'"
              @click="toggleStatus('temporary,deleted_draft')"
              :class="[
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 border',
                activeStatusFilter === 'temporary,deleted_draft'
                  ? 'shadow-lg bg-securdia-blue text-white border-securdia-blue'
                  : 'bg-white text-gray-800 border-gray-300 shadow-sm hover:bg-gray-100'
              ]"
            >
              <span class="font-bold">{{ getDraftCount() }}</span>
              <span>{{ $t('agreements.temporary') }}</span>
            </button>
            <!-- Short-term rental filter (contract period 1-6 months), combinable with status -->
            <button
              @click="toggleShortTerm"
              :class="[
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 border',
                shortTermFilter
                  ? 'shadow-lg bg-securdia-blue text-white border-securdia-blue'
                  : 'bg-white text-gray-800 border-gray-300 shadow-sm hover:bg-gray-100'
              ]"
            >
              <span class="font-bold">{{ getShortTermCount() }}</span>
              <span>{{ $t('agreements.short_term_rental') }}</span>
            </button>
            <!-- My Agreements filter (sellers only) -->
            <button 
              v-if="mySellerRecord !== null"
              @click="toggleMyAgreements" 
              :class="[
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 border',
                isMyAgreementsActive 
                  ? 'shadow-lg bg-green-600 text-white border-green-600' 
                  : 'bg-white text-green-700 border-green-400 shadow-sm hover:bg-green-50'
              ]"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
              <span>{{ $t('agreements.my_agreements') }}</span>
            </button>
            <!-- Help / Info button -->
            <button
              @click.stop="showHelpModal = true"
              class="ml-auto inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-securdia-blue hover:bg-blue-50 transition-colors"
              :title="$t('agreements.help_modal.title')"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>



      <!-- Agreements Table -->
      <div class="sd-card p-0 overflow-x-auto">
        <vue-good-table
          ref="agreementsTable"
          styleClass="vgt-table sd-table sd-table--compact"
          :mode="isAdminOrSeller ? 'remote' : 'records'"
          :row-style-class="getRowStyleClass"
          :columns="translatedColumns"
          :rows="filteredAgreements"
          :totalRows="isAdminOrSeller ? totalRecords : filteredAgreements.length"
          :isLoading.sync="isLoading"
          :search-options="{
            enabled: true,
            placeholder: $t('agreements.search_placeholder') || 'Sök avtal, kundnamn, org.nr...'
          }"
          :pagination-options="{
            enabled: true,
            mode: isAdminOrSeller ? 'remote' : 'records',
            perPage: pageSize,
            perPageDropdown: [50, 100, 200, 500],
            dropdownAllowAll: false,
            setCurrentPage: currentPage,
            jumpFirstOrLast: true
          }"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
          @on-search="onSearch"
        >
          <template slot="table-actions">
            <div></div>
          </template>
          <template slot="table-column" slot-scope="props">
            <span v-if="props.column.field === 'select'">
              <button 
                v-if="selectedAgreementIds.length > 0 "
                @click.prevent.stop="selectedAgreementIds = []"
                class="flex items-center justify-center w-5 h-5 text-red-500 hover:text-red-700 transition-colors"
                :title="$t('agreements.deselect_all') || 'Avmarkera alla'"
                >
              <BaseIcon icon="times-circle"/>
              </button>
            </span>
            <span v-else>
              {{ props.column.label }}
            </span>
          </template>
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field == 'select'">
              <input 
                type="checkbox"
                :checked="selectedAgreementIds.includes(props.row._id)"
                @change="toggleAgreementSelection(props.row._id)"
                class="w-4 h-4 text-blue-600 rounded cursor-pointer"
                @click.stop
              />
            </span>
            <span v-else-if="props.column.field == 'agreement_number'" class="flex items-center gap-2">
              <!-- Indent marker for child rows -->
              <span v-if="props.row._isChildRow" class="text-blue-400 ml-4">
                └
              </span>
              <!-- Dropdown trigger for parent agreements with children -->
              <button 
                v-if="props.row.child_agreement_numbers && props.row.child_agreement_numbers.length > 0 && !props.row._isChildRow"
                @click.prevent.stop="toggleChildAgreements(props.row.agreement_number)"
                class="text-gray-500 hover:text-gray-700 focus:outline-none"
                :title="$t('agreements.show_child_agreements')"
              >
                <BaseIcon :icon="expandedParents.includes(props.row.agreement_number) ? 'caret-down' : 'caret-right'" />
              </button>
              
              <span 
                @click.prevent="showAgreementAlarm(props.row.agreement_number)" 
                :class="props.row.status === 'active_old' ? 'text-gray-400 cursor-pointer' : 'link'"
                :style="props.row.status === 'active_old' ? 'text-decoration: line-through;' : ''"
              >
                {{ props.row.agreement_number }}
              </span>
            </span>
            <span v-else-if="props.column.field == 'alarm_count' && (user.role == 'admin' || user.role == 'seller') && props.row.status !== 'temporary'">
              {{ getAlarmCount(props.row.agreement_number) }}
            </span>
            <span v-else-if="props.column.field == 'status'">
              <span 
                :class="[getStatusBadgeClass(props.row.status), hasDismissalPdf(props.row) ? 'cursor-pointer hover:opacity-80' : '']"
                @click="hasDismissalPdf(props.row) ? openDismissalPdf(props.row) : null"
              >
                {{ getStatusLabel(props.row.status) }}
              </span>
            </span>
            <span v-else-if="props.column.field == 'insurance'">
              <!-- No badge at all when the agreement has no insurance field:
                   that is "not recorded", not the same as an explicit false. -->
              <template v-if="props.row.status !== 'temporary' && props.row.insurance != null">
                <span
                  v-if="props.row.insurance"
                  class="inline-block px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded"
                  :class="props.row.insurance_removal_pdf ? 'cursor-pointer hover:opacity-80' : ''"
                  @click="props.row.insurance_removal_pdf ? openInsurancePdf(props.row) : null"
                >
                  INS
                </span>
                <span v-else class="inline-block px-2 py-1 text-xs font-semibold text-gray-400 bg-gray-100 rounded" style="text-decoration: line-through;"
                  :class="props.row.insurance_removal_pdf ? 'cursor-pointer hover:opacity-80' : ''"
                  @click="props.row.insurance_removal_pdf ? openInsurancePdf(props.row) : null"
                >
                  INS
                </span>
              </template>
            </span>
            <span v-else-if="props.column.field == 'invoice' && (user.role == 'admin' || user.role == 'seller')">
              <!-- Special menu for temporary/draft agreements -->
              <div v-if="props.row.status === 'temporary' && canActOnAgreement(props.row)" class="flex items-center gap-2 -ml-2">
                <div class="relative inline-block">
                  <button 
                    class="btn-secondary-outline p-2"
                    @click.prevent="toggleMenu(props.row.agreement_number, $event)"
                  >
                    <BaseIcon icon="bars" />
                  </button>
                </div>
              </div>
              <!-- Regular menu for non-temporary agreements -->
              <div v-else-if="canActOnAgreement(props.row) && props.row.status !== 'temporary' && props.row.status !== 'deleted_draft'" class="flex items-center gap-2 -ml-2">
                <div class="relative inline-block">
                  <button 
                    
                    class="btn-secondary-outline p-2"
                    @click.prevent="toggleMenu(props.row.agreement_number, $event)"
                    :ref="'menuBtn_' + props.row.agreement_number"
                  >
                    <BaseIcon icon="bars" />
                  </button>
                </div>
                <div 
                  v-if="props.row.invoice_date == null && user.role === 'admin'"
                  class="relative inline-block"
                >
                  <button 
                    class="btn-blue-outline p-2"
                    @click.prevent="sendInvoiceEmailPrompt(props.row)"
                  >
                    <BaseIcon icon="file-invoice-dollar" />
                  </button>
                </div>
                <div
                  v-else-if="props.row.invoice_date != null"
                  class="relative inline-block"
                  @mouseenter="props.row.invoice_by_name ? (hoveredInvoiceDate = props.row.agreement_number) : null"
                  @mouseleave="hoveredInvoiceDate = null"
                >
                  <span class="text-xs cursor-default">{{ formatDate(props.row.invoice_date) }}</span>
                  <div
                    v-if="props.row.invoice_by_name && hoveredInvoiceDate === props.row.agreement_number"
                    class="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                    style="z-index: 99999;"
                  >
                    {{ props.row.invoice_by_name }}
                    <div class="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="relative inline-block"
                @mouseenter="props.row.invoice_by_name ? (hoveredInvoiceDate = props.row.agreement_number) : null"
                @mouseleave="hoveredInvoiceDate = null"
              >
                <span class="text-xs text-gray-400 cursor-default">{{ props.row.invoice_date ? formatDate(props.row.invoice_date) : '' }}</span>
                <div
                  v-if="props.row.invoice_by_name && hoveredInvoiceDate === props.row.agreement_number"
                  class="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                  style="z-index: 99999;"
                >
                  {{ props.row.invoice_by_name }}
                  <div class="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </span>
            <span v-else-if="props.column.field == 'action'">
              <a v-if="props.row.pdf_file != null" :href="props.row.pdf_file" download>
                <BaseIcon icon="file-pdf" class="text-red-600 text-xl cursor-pointer" />
              </a>
            </span>
            <span v-else-if="props.column.field == 'edit' && (user.role == 'admin' || user.role == 'seller')">
              <button v-if="canActOnAgreement(props.row)" class="sd-btn sd-btn--icon sd-btn--secondary" @click.prevent="showEdit(props.row)">
                <BaseIcon icon="pencil-alt" />
              </button>
            </span>
            <div
              v-else-if="props.column.field == 'customer_name'"
              class="relative inline-block"
              @mouseenter="hoveredNameRow = props.row._id"
              @mouseleave="hoveredNameRow = null"
            >
              <span class="block truncate" style="max-width: 140px;">{{ props.row.customer_name }}</span>
              <div
                v-if="hoveredNameRow === props.row._id"
                class="absolute left-0 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                style="z-index: 99999;"
              >
                {{ props.row.customer_name }}
                <div class="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
            <span v-else-if="props.column.field == 'seller'" class="whitespace-pre-line leading-tight">{{ formatSellerDisplay(props.formattedRow[props.column.field]) }}</span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
          <div slot="emptystate" class="flex flex-wrap flex-col items-center my-5 text-sm text-gray-500">
            {{ $t("my_agreement_table.no_agreement_found") }}
          </div>
        </vue-good-table>
      </div>
    </div>

    <!-- Dropdown menu outside table structure -->
    <div 
      v-if="openMenuId !== null"
      ref="agreementsDropdown"
      class="fixed w-56 bg-white border border-gray-100 rounded-xl shadow-2xl overflow-y-auto"
      style="z-index: 99999; max-height: calc(100vh - 16px);"
      :style="{ top: menuPosition.top, left: menuPosition.left }"
      @click.stop
    >
      <div class="py-1">
        <!-- Draft/Temporary agreement menu options -->
        <template v-if="selectedAgreementForMenu?.status === 'temporary'">
          <button 
            class="w-full text-left px-4 py-2.5 hover:bg-green-50 text-sm transition-colors flex items-center gap-2 text-green-700"
            @click.prevent="handleMenuAction('activate_draft', selectedAgreementForMenu)"
          >
            <BaseIcon icon="check-circle" class="text-green-400" />
            {{ $t('agreements.menu.activate_draft') }}
          </button>
          <button 
            class="w-full text-left px-4 py-2.5 hover:bg-red-50 text-sm transition-colors flex items-center gap-2 text-red-600"
            @click.prevent="handleMenuAction('delete_draft', selectedAgreementForMenu)"
          >
            <BaseIcon icon="trash-alt" class="text-red-400" />
            {{ $t('agreements.menu.delete_draft') }}
          </button>
        </template>

        <!-- Regular agreement menu options -->
        <template v-else>
        <button 
          v-if="selectedAgreementForMenu?.status !== 'dismissed' && selectedAgreementForMenu?.status !== 'returned'"
          class="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm transition-colors flex items-center gap-2 text-gray-700"
          @click.prevent="handleMenuAction('terminate', selectedAgreementForMenu)"
        >
          <BaseIcon icon="ban" class="text-gray-400" />
          {{ $t('agreements.menu.terminate') }}
        </button>
        <button 
          v-if="selectedAgreementForMenu?.status !== 'dismissed' && selectedAgreementForMenu?.status !== 'returned'"
          class="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm transition-colors flex items-center gap-2 text-gray-700"
          @click.prevent="handleMenuAction('cancel', selectedAgreementForMenu)"
        >
          <BaseIcon icon="times-circle" class="text-gray-400" />
          {{ $t('agreements.menu.cancel') }}
        </button>
        <button 
          v-if="selectedAgreementForMenu?.insurance === true && selectedAgreementForMenu?.status !== 'dismissed' && selectedAgreementForMenu?.status !== 'returned'"
          class="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm transition-colors flex items-center gap-2 text-gray-700"
          @click.prevent="handleMenuAction('report_damage', selectedAgreementForMenu)"
        >
          <BaseIcon icon="exclamation-circle" class="text-gray-400" />
          {{ $t('agreements.menu.report_damage') }}
        </button>
        <div v-if="selectedAgreementForMenu?.insurance === true && selectedAgreementForMenu?.status !== 'dismissed' && selectedAgreementForMenu?.status !== 'returned'" class="border-t border-gray-100 my-1"></div>
        <button 
          v-if="selectedAgreementForMenu?.insurance === false"
          class="w-full text-left px-4 py-2.5 hover:bg-green-50 text-sm transition-colors flex items-center gap-2 text-green-700"
          @click.prevent="handleMenuAction('add_insurance', selectedAgreementForMenu)"
        >
          <BaseIcon icon="shield-alt" class="text-green-400" />
          {{ $t('agreements.menu.add_insurance') }}
        </button>
        <button 
          v-if="selectedAgreementForMenu?.insurance === true"
          class="w-full text-left px-4 py-2.5 hover:bg-red-50 text-sm transition-colors flex items-center gap-2 text-red-600"
          @click.prevent="handleMenuAction('remove_insurance', selectedAgreementForMenu)"
        >
          <BaseIcon icon="shield-alt" class="text-red-400" />
          {{ $t('agreements.menu.remove_insurance') }}
        </button>
        <button 
          v-if="selectedAgreementForMenu?.status === 'dismissed' || (selectedAgreementForMenu?.status === 'returned')"
          class="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-sm transition-colors flex items-center gap-2 text-blue-600"
          @click.prevent="handleMenuAction('reActivate', selectedAgreementForMenu)"
          >
          <BaseIcon icon="check-circle" class="text-blue-400" />
          {{ $t('agreements.menu.reactivate') }}
        </button>
        <button 
          v-if="selectedAgreementForMenu?.status === 'dismissed' && getAlarmCount(selectedAgreementForMenu?.agreement_number) === 0"
          class="w-full text-left px-4 py-2.5 hover:bg-green-50 text-sm transition-colors flex items-center gap-2 text-green-600"
          @click.prevent="handleMenuAction('end_now', selectedAgreementForMenu)"
        >
          <BaseIcon icon="check-circle" class="text-green-400" />
          {{ $t('agreements.menu.end_now') }}
        </button>
        <div v-if="!['returned', 'active_old'].includes(selectedAgreementForMenu?.status)" class="border-t border-gray-100 my-1"></div>
        <button 
          v-if="!['returned', 'active_old'].includes(selectedAgreementForMenu?.status)"
          class="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-sm transition-colors flex items-center gap-2 text-blue-600"
          @click.prevent="handleMenuAction('redraw', selectedAgreementForMenu)"
        >
          <BaseIcon icon="sync-alt" class="text-blue-400" />
          {{ $t('agreements.menu.redraw_agreement') }}
        </button>
        <button 
          v-if="selectedAgreementForMenu?.invoice_date != null && selectedAgreementForMenu?.status !== 'extended' && (selectedAgreementForMenu?.commission_noted === false || selectedAgreementForMenu?.commission_noted === null) && (selectedAgreementForMenu?.pdf_file != null || user.role === 'admin')"
          class="w-full text-left px-4 py-2.5 hover:bg-green-50 text-sm transition-colors flex items-center gap-2 text-green-600"
          @click.prevent="handleMenuAction('note_commission', selectedAgreementForMenu)"
        >
          <BaseIcon icon="smile" class="text-green-400" />
          {{ $t('agreements.menu.note_commission') }}
        </button>
        <button 
          v-if="selectedAgreementForMenu?.invoice_date != null"
          class="w-full text-left px-4 py-2.5 hover:bg-red-50 text-sm transition-colors flex items-center gap-2 text-red-600"
          @click.prevent="handleMenuAction('undo_invoice', selectedAgreementForMenu)"
        >
          <BaseIcon icon="undo" class="text-red-400" />
          {{ $t('agreements.menu.undo_invoice') }}
        </button>
        </template>
      </div>
    </div>

    <!-- Help / Info Modal -->
    <div
      v-if="showHelpModal"
      class="fixed inset-0 flex items-center justify-center"
      style="z-index: 100000;"
    >
      <div class="absolute inset-0 bg-black bg-opacity-40" @click="showHelpModal = false"></div>
      <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-screen-85 overflow-y-auto m-4" style="max-height: 85vh;">
        <!-- Sticky header -->
        <div class="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-securdia-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <h2 class="text-lg font-semibold text-gray-800">{{ $t('agreements.help_modal.title') }}</h2>
          </div>
          <button @click="showHelpModal = false" class="text-gray-400 hover:text-gray-600 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <!-- Content -->
        <div class="px-6 py-5 space-y-8">

          <!-- Statuses -->
          <section>
            <h3 class="text-xs font-bold uppercase tracking-wider text-securdia-blue mb-3">{{ $t('agreements.help_modal.statuses_title') }}</h3>
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-gray-50 border-b border-gray-200">
                  <th class="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2 w-32">{{ $t('agreements.help_modal.col_status') }}</th>
                  <th class="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">{{ $t('agreements.help_modal.col_description') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="inline-block px-2 py-0.5 text-xs font-semibold rounded text-yellow-700 bg-yellow-100">{{ $t('agreements.pending') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.pending_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="inline-block px-2 py-0.5 text-xs font-semibold rounded text-green-700 bg-green-100">{{ $t('agreements.active') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.active_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="inline-block px-2 py-0.5 text-xs font-semibold rounded text-blue-700 bg-blue-100">{{ $t('agreements.extended') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.extended_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="inline-block px-2 py-0.5 text-xs font-semibold rounded text-purple-700 bg-purple-100">{{ $t('agreements.redrawn') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.redrawn_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="inline-block px-2 py-0.5 text-xs font-semibold rounded text-orange-700 bg-orange-100">{{ $t('agreements.dismissed') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.dismissed_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="inline-block px-2 py-0.5 text-xs font-semibold rounded text-gray-700 bg-gray-200">{{ $t('agreements.returned') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.returned_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="inline-block px-2 py-0.5 text-xs font-semibold rounded text-gray-400 bg-gray-100">{{ $t('agreements.active_old') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.active_old_desc') }}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <!-- Insurance (INS) badges -->
          <section>
            <h3 class="text-xs font-bold uppercase tracking-wider text-securdia-blue mb-3">{{ $t('agreements.help_modal.insurance_title') }}</h3>
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-gray-50 border-b border-gray-200">
                  <th class="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2 w-32">{{ $t('agreements.help_modal.col_marking') }}</th>
                  <th class="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">{{ $t('agreements.help_modal.col_description') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="inline-block px-2 py-0.5 text-xs font-semibold rounded text-blue-700 bg-blue-100">INS</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.ins_yes_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="inline-block px-2 py-0.5 text-xs font-semibold rounded text-gray-400 bg-gray-100" style="text-decoration: line-through;">INS</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.ins_no_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap text-gray-400">—</td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.ins_none_desc') }}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <!-- Menu Actions -->
          <section>
            <h3 class="text-xs font-bold uppercase tracking-wider text-securdia-blue mb-3">{{ $t('agreements.help_modal.menu_actions_title') }}</h3>
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-gray-50 border-b border-gray-200">
                  <th class="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2 w-40">{{ $t('agreements.help_modal.col_action') }}</th>
                  <th class="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">{{ $t('agreements.help_modal.col_description') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{{ $t('agreements.menu.terminate') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.terminate_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{{ $t('agreements.menu.cancel') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.cancel_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{{ $t('agreements.menu.report_damage') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.report_damage_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">{{ $t('agreements.menu.add_insurance') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.add_insurance_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded">{{ $t('agreements.menu.remove_insurance') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.remove_insurance_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{{ $t('agreements.menu.reactivate') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.reactivate_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded">{{ $t('agreements.menu.end_now') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.end_now_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{{ $t('agreements.menu.redraw_agreement') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.redraw_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded">{{ $t('agreements.menu.note_commission') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.note_commission_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 whitespace-nowrap"><span class="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded">{{ $t('agreements.menu.undo_invoice') }}</span></td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.undo_invoice_desc') }}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <!-- Filters -->
          <section>
            <h3 class="text-xs font-bold uppercase tracking-wider text-securdia-blue mb-3">{{ $t('agreements.help_modal.filters_title') }}</h3>
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-gray-50 border-b border-gray-200">
                  <th class="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2 w-40">{{ $t('agreements.help_modal.col_filter') }}</th>
                  <th class="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">{{ $t('agreements.help_modal.col_description') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 font-semibold text-gray-800 whitespace-nowrap">{{ $t('agreements.help_modal.filter_expiry_title') }}</td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.filter_expiry_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 font-semibold text-gray-800 whitespace-nowrap">{{ $t('agreements.help_modal.filter_status_title') }}</td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.filter_status_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 font-semibold text-gray-800 whitespace-nowrap">{{ $t('agreements.help_modal.filter_short_term_title') }}</td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.filter_short_term_desc') }}</td>
                </tr>
                <tr v-if="canFilterDutch" class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 font-semibold text-gray-800 whitespace-nowrap">{{ $t('country_origin.help_title') }}</td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('country_origin.help_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 font-semibold text-gray-800 whitespace-nowrap">{{ $t('agreements.help_modal.filter_my_agreements_title') }}</td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.filter_my_agreements_desc') }}</td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="px-3 py-2.5 font-semibold text-gray-800 whitespace-nowrap">{{ $t('agreements.help_modal.filter_advanced_title') }}</td>
                  <td class="px-3 py-2.5 text-gray-600">{{ $t('agreements.help_modal.filter_advanced_desc') }}</td>
                </tr>
              </tbody>
            </table>
          </section>

        </div>
      </div>
    </div>
    <AgreementUpdate :agreementInfo="updateInfo" @updateAgreement="getAgreements()" />
    <AgreementRedraw :agreementInfo="selectedAgreementForRedraw" @agreementRedrawn="getAgreements()" />
    <AgreementAlarm :agreementNumber="selectedAgreementNumber" />
    <AgreementFilter :filters="advancedFilters" :sellers="staffSellers" :resellers="resellersList" @filter-changed="handleFilterChange" />
    <AgreementsWarnings :warnings="warningAgreements" :canEdit="user.role === 'admin' || user.role === 'seller'" :canActFn="canActOnAgreement" @edit-agreement="showEdit" @open-menu="openMenuFromWarnings"/>
    <ReportDamage :agreement="selectedAgreementForDamage" @damage-reported="getAgreements()" />
    <RemoveInsurance :agreement="selectedAgreementForInsuranceRemoval" @insurance-removed="getAgreements()" />
    <CancelAgreement :agreement="selectedAgreementForCancellation" @agreement-cancelled="getAgreements()" />
    <DismissAgreement :agreement="selectedAgreementForDismissal" @agreement-dismissed="getAgreements()" />
    <EndAgreement :agreement="selectedAgreementForEndNow" @agreement-ended="getAgreements()" />
    <AgreementMarkMultiple :agreements="agreements" @agreements-updated="selectedAgreementIds = []; getAgreements()" />
    <NoteCommission :alarmCenters="alarm_centers" :sellers="sellers || []" @commission-noted="getAgreements()" />
    <CreateTemporaryAgreement v-if="user.role === 'admin'" :sellers="sellers || []" @agreement-created="getAgreements()" />
    <ActivateTemporaryAgreement v-if="user.role === 'admin'" :sellers="sellers || []" :alarm_centers="alarm_centers || []" @agreement-activated="getAgreements()" />
  </div>
</template>

<script>
import AgreementUpdate from "@/components/modal/agreement_update";
import AgreementRedraw from "@/components/modal/agreement_redraw";
import CreateTemporaryAgreement from "@/components/modal/create_temporary_agreement";
import ActivateTemporaryAgreement from "@/components/modal/activate_temporary_agreement";
import AgreementAlarm from "@/components/modal/agreement_alarm";
import AgreementFilter from "@/components/modal/agreement_filter";
import AgreementsWarnings from "@/components/modal/agreements_warnings";
import ReportDamage from "@/components/modal/report_damage";
import RemoveInsurance from "@/components/modal/remove_insurance";
import { canFilterDutch } from "@/utils/countryOrigin";
import CancelAgreement from "@/components/modal/cancel_agreement";
import DismissAgreement from "@/components/modal/dismiss_agreement";
import EndAgreement from "@/components/modal/end_agreement";
import AgreementMarkMultiple from "@/components/modal/agreement_mark_multiple";
import NoteCommission from "@/components/modal/note_commission";

export default {
  title() {
    return this.$t("page_titles.agreement");
  },
  components: {
    AgreementUpdate,
    AgreementRedraw,
    AgreementAlarm,
    AgreementFilter,
    AgreementsWarnings,
    ReportDamage,
    RemoveInsurance,
    CancelAgreement,
    DismissAgreement,
    EndAgreement,
    AgreementMarkMultiple,
    NoteCommission,
    CreateTemporaryAgreement,
    ActivateTemporaryAgreement
  },
  data() {
    return {
      id: this.$route.params.id,
      type: this.$route.params.type,
      isLoading: false,
      showWarningTooltip: false,
      agreements: [],
      alarm_centers: [],
      agreement_alarms: [],
      sellers: [],
      updateInfo: null,
      selectedAgreementNumber: null,
      selectedAgreementForDamage: null,
      selectedAgreementForInsuranceRemoval: null,
      selectedAgreementForCancellation: null,
      selectedAgreementForDismissal: null,
      selectedAgreementForMenu: null,
      selectedAgreementForEndNow: null,
      selectedAgreementForRedraw: null,
      selectedAgreementForCommission: null,
      showHelpModal: false,
      hoveredInvoiceButton: null, // Track which invoice button is being hovered for tooltip
      hoveredInvoiceDate: null, // Track which invoice date is being hovered for invoice_by_name tooltip
      hoveredNameRow: null, // Track which name cell is being hovered for tooltip
      selectedAgreementIds: [], // Track checked agreements in table for bulk actions
      expandedParents: [], // Track which parent agreements have their children expanded
      menuPosition: { top: '0px', left: '0px' },
      activeFilter: null, // null, 6, 12, or 24 months
      activeStatusFilter: null, // null or status string ('active', 'dismissed', etc.)
      shortTermFilter: false, // "Korttidshyra": contract period 1-6 months
      agreementView: null, // deprecated (status filter moved to filter modal)
      openMenuId: null, // Track which menu is open
      advancedFilters: {
        pdfOnly: null,
        invoiceSent: null,
        hasAlarms: null,
        hasInsurance: null,
        invoiceWithoutPdf: null,
        withoutAlarms: null,
        dismissedPastCancellation: null,
        dismissedWithDevices: null,
        selectedSeller: null,
        selectedReseller: null,
        customerSearch: '',
        status: null,
        countryOrigin: null, // 'se' / 'nl': Swedish or Dutch customers' agreements (utils/countryOrigin)
      },
      // Pagination
      currentPage: 1,
      pageSize: 100,
      totalRecords: 0,
      totalPages: 0,
      searchQuery: '',
      statusFilter: 'all',
      activeCategoryFilter: 'all',
      // Server-side stats (calculated on ALL agreements, not just paginated)
      serverStats: null,
      // Server-fetched warning agreements for admin (fetched on-demand when modal opens)
      serverWarningAgreements: null,
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    // help entry of the country filter in the filter modal (utils/countryOrigin)
    canFilterDutch() {
      return canFilterDutch(this.user);
    },
    // Convenience computed: true when user is admin or seller (both use server-side pagination)
    isAdminOrSeller() {
      return this.user.role === 'admin' || this.user.role === 'seller';
    },
    staffSellers() {
      return (this.sellers || []).filter(s => s._entityType === 'staff');
    },
    resellersList() {
      return (this.sellers || []).filter(s => s._entityType === 'reseller');
    },
    // Resolves the Staff/seller record for the current user:
    // - Seller role: matched by seller_id
    // - Admin role: matched by email (admin users who are also registered sellers)
    // Returns null if no matching seller is found or sellers aren't loaded yet.
    mySellerRecord() {
      if (!this.sellers || !this.sellers.length) return null;
      if (this.user.role === 'seller' && this.user.seller_id) {
        return this.sellers.find(s => s._id === this.user.seller_id) || null;
      }
      if (this.user.role === 'admin' && this.user.email) {
        return this.sellers.find(s => s.email && s.email.toLowerCase() === this.user.email.toLowerCase()) || null;
      }
      return null;
    },
    // True when the "My Agreements" quick filter is active (seller's own ID is set in advancedFilters)
    isMyAgreementsActive() {
      return !!this.mySellerRecord && this.advancedFilters.selectedSeller === this.mySellerRecord._id;
    },
    // Pre-compute alarm counts as a Map for O(1) lookups instead of O(n) _.filter per call
    alarmCountMap() {
      const map = {};
      if (!this.agreement_alarms) return map;
      for (const alarm of this.agreement_alarms) {
        if (!map[alarm.agreement_number]) {
          map[alarm.agreement_number] = 0;
        }
        map[alarm.agreement_number]++;
      }
      return map;
    },
    translatedColumns() {
      const isCustomer = this.user.role === 'superuser' || this.user.role === 'user';
      const isAdmin = this.user.role === 'admin';
      return [
        { label: '', field: 'select', sortable: false, width: '40px', hidden: !isAdmin },
        { label: this.$t("my_agreement_table.agreement_number"), field: "agreement_number", sortable: false, width: '135px' },
        { label: this.$t("my_agreement_table.name"), field: "customer_name", sortable: false, width: '150px' },
        { label: this.$t("my_agreement_table.contract_period"), field: "contract_period", sortable: false, width: '75px' },
        { label: this.$t("my_agreement_table.contract_start"), field: "contract_start", sortable: false, formatFn: this.formatOptionalDate, width: '115px' },
        { label: this.$t("my_agreement_table.contact_person"), field: "customer_contact", sortable: false, width: '90px' },
        { label: this.$t("my_agreement_table.is_reference"), field: "customer_reference", sortable: false, width: '55px' },
        { label: this.$t("my_agreement_table.seller"), field: "seller", sortable: false, formatFn: this.getSellerName, width: '100px' },
        { label: this.$t("agreements.status"), field: "status", sortable: false, hidden: isCustomer, width: '85px' },
        { label: this.$t("my_agreement_table.rental_month_cost"), field: "rental_month_cost", sortable: false, formatFn: this.formatAmountField, hidden: isCustomer, width: '80px' },
        { label: this.$t("my_agreement_table.insurance"), field: "insurance", sortable: false, width: "50px", hidden: isCustomer, tdClass: "!px-1", thClass: "!px-1" },
        { label: "", field: "invoice", sortable: false, width: '100px', tdClass: "!px-1", thClass: "!px-1" },
        { label: "", field: "alarm_count", sortable: false, width: "40px", tdClass: "!px-1", thClass: "!px-1" },
        { label: "", field: "action", sortable: false, width: "36px", tdClass: "!px-1", thClass: "!px-1" },
        { label: "", field: "edit", sortable: false, tdClass: "text-left !px-1", thClass: "!px-1", width: "42px" },
      ];
    },
    filteredAgreements() {
      let filtered = this.agreements;

      // NOTE: agreements for the 'user' role are already scoped server-side by
      // getUnitsAgreement (by units_id, multi-customer aware). No client-side
      // name-matching filter — it under-showed valid agreements.

      if (this.advancedFilters.status && !this.isAdminOrSeller) {
        filtered = filtered.filter(agreement => agreement.status === this.advancedFilters.status);
      }

      // Active category filter (financed / PIF)
      if (this.activeCategoryFilter && this.activeCategoryFilter !== 'all') {
        filtered = filtered.filter(agreement => this.matchesActiveCategory(agreement, this.activeCategoryFilter));
      }

      if (!this.isAdminOrSeller) {
        if (this.advancedFilters.pdfOnly === 'yes') {
          filtered = filtered.filter(agreement => agreement.pdf_file != null);
        } else if (this.advancedFilters.pdfOnly === 'no') {
          filtered = filtered.filter(agreement => agreement.pdf_file == null);
        }

        if (this.advancedFilters.invoiceSent === 'yes') {
          filtered = filtered.filter(agreement => agreement.invoice_date != null);
        } else if (this.advancedFilters.invoiceSent === 'no') {
          filtered = filtered.filter(agreement => agreement.invoice_date == null);
        }
        
        if (this.advancedFilters.hasInsurance === 'yes') {
          filtered = filtered.filter(agreement => agreement.insurance === true);
        } else if (this.advancedFilters.hasInsurance === 'no') {
          filtered = filtered.filter(agreement => agreement.insurance !== true);
        }

        if (this.advancedFilters.hasAlarms === 'yes') {
          filtered = filtered.filter(agreement => {
            const alarmCount = this.getAlarmCount(agreement.agreement_number);
            return alarmCount > 0;
          });
        } else if (this.advancedFilters.hasAlarms === 'no') {
          filtered = filtered.filter(agreement => {
            const alarmCount = this.getAlarmCount(agreement.agreement_number);
            return alarmCount === 0;
          });
        }

        if (this.advancedFilters.selectedSeller) {
          filtered = filtered.filter(agreement => agreement.seller === this.advancedFilters.selectedSeller);
        } else if (this.advancedFilters.selectedReseller) {
          filtered = filtered.filter(agreement => agreement.seller === this.advancedFilters.selectedReseller);
        }

        if (this.advancedFilters.customerSearch) {
          const search = this.advancedFilters.customerSearch.toLowerCase();
          filtered = filtered.filter(agreement => 
            (agreement.customer_name && agreement.customer_name.toLowerCase().includes(search)) ||
            (agreement.customer_contact && agreement.customer_contact.toLowerCase().includes(search))
          );
        }

        // Warning filters - reuse already calculated warnings from warningAgreements
        const includeWarningFilters = [];
        const excludeWarningFilters = [];
        
        if (this.advancedFilters.invoiceWithoutPdf === 'yes') includeWarningFilters.push('invoice_without_pdf');
        else if (this.advancedFilters.invoiceWithoutPdf === 'no') excludeWarningFilters.push('invoice_without_pdf');
        
        if (this.advancedFilters.withoutAlarms === 'yes') includeWarningFilters.push('without_alarms');
        else if (this.advancedFilters.withoutAlarms === 'no') excludeWarningFilters.push('without_alarms');
        
        if (this.advancedFilters.dismissedPastCancellation === 'yes') includeWarningFilters.push('dismissed_past_cancellation_date');
        else if (this.advancedFilters.dismissedPastCancellation === 'no') excludeWarningFilters.push('dismissed_past_cancellation_date');
        
        if (this.advancedFilters.dismissedWithDevices === 'yes') includeWarningFilters.push('dismissed_with_devices');
        else if (this.advancedFilters.dismissedWithDevices === 'no') excludeWarningFilters.push('dismissed_with_devices');

        if (includeWarningFilters.length > 0) {
          // Get agreement numbers that HAVE the selected warnings
          const warningAgreementNumbers = this.warningAgreements
            .filter(warning => includeWarningFilters.some(filter => warning.warning_keys.includes(filter)))
            .map(warning => warning.agreement_number);
          
          filtered = filtered.filter(agreement => warningAgreementNumbers.includes(agreement.agreement_number));
        }

        if (excludeWarningFilters.length > 0) {
          // Get agreement numbers that have warnings to exclude
          const excludeAgreementNumbers = this.warningAgreements
            .filter(warning => excludeWarningFilters.some(filter => warning.warning_keys.includes(filter)))
            .map(warning => warning.agreement_number);
          
          filtered = filtered.filter(agreement => !excludeAgreementNumbers.includes(agreement.agreement_number));
        }
      } // End of if (!this.isAdminOrSeller) block

      // Insert child agreements after expanded parent rows
      const result = [];
      for (const agreement of filtered) {
        result.push(agreement);
        
        // If this parent agreement is expanded, insert its child agreements
        if (this.expandedParents.includes(agreement.agreement_number) && 
            agreement.child_agreement_numbers && 
            agreement.child_agreement_numbers.length > 0) {
          
          // Find child agreements from the full agreements list
          for (const childNumber of agreement.child_agreement_numbers) {
            const childAgreement = this.agreements.find(a => a.agreement_number === childNumber);
            if (childAgreement) {
              // Mark as child for styling purposes
              result.push({
                ...childAgreement,
                _isChildRow: true,
                _parentAgreementNumber: agreement.agreement_number
              });
            }
          }
        }
      }

      return result;
    },
    activeCategoryFilterLabel() {
      switch (this.activeCategoryFilter) {
        case 'financed':
          return this.$t('agreements.total_active_filter_financed');
        case 'pif':
          return this.$t('agreements.total_active_filter_pif');
        default:
          return '';
      }
    },

    totalWarnings() {
      if (this.isAdminOrSeller && this.serverStats?.warningCounts) {
        const wc = this.serverStats.warningCounts;
        return wc.invoiceWithoutPdf + wc.withoutAlarms + wc.extended + wc.dismissedPastCancellation + wc.dismissedWithDevices;
      }
      return this.invoiceWithoutPdfCount + this.agreementsWithoutAlarmsCount + this.extendedAgreementsCount + this.dismissedAgreementsPastExpDate + this.dismissedWithDevices;
    },

    invoiceWithoutPdfCount() {
      if (this.isAdminOrSeller && this.serverStats?.warningCounts) {
        return this.serverStats.warningCounts.invoiceWithoutPdf;
      }
      return this.agreements.filter(agreement => 
        agreement.invoice_date != null && agreement.pdf_file == null
      ).length;
    },

    dismissedWithDevices(){
      if (this.isAdminOrSeller && this.serverStats?.warningCounts) {
        return this.serverStats.warningCounts.dismissedWithDevices;
      }
      const in_one_month = this.moment().add(1, 'month');

      return this.agreements.filter(agreement => {
        if(this.getAlarmCount(agreement.agreement_number) > 0 && agreement.status === 'dismissed' && this.moment(agreement.dismissal_info[0].dismissal_date).isBefore(in_one_month)){
          return true;
        }
        return false;
        }).length;
      },

    dismissedAgreementsPastExpDate(){
      if (this.isAdminOrSeller && this.serverStats?.warningCounts) {
        return this.serverStats.warningCounts.dismissedPastCancellation;
      }
      const now = this.moment();
      return this.agreements.filter(agreement => {
        if (!agreement.dismissal_info || !agreement.dismissal_info[0] || !agreement.dismissal_info[0].dismissal_date) {
          return false;
        }
        const dismissalDate = this.moment(agreement.dismissal_info[0].dismissal_date);
        return dismissalDate.isBefore(now, 'day');
      }).length;
    },

    agreementsWithoutAlarmsCount() {
      if (this.isAdminOrSeller && this.serverStats?.warningCounts) {
        return this.serverStats.warningCounts.withoutAlarms;
      }
      return this.agreements.filter(agreement => {
        const alarmCount = this.getAlarmCount(agreement.agreement_number);
        return alarmCount === 0;
      }).length;
    },


    // Count the agreements with status = extended
    extendedAgreementsCount() {
      if (this.isAdminOrSeller && this.serverStats?.warningCounts) {
        return this.serverStats.warningCounts.extended;
      }
      return this.agreements.filter(agreement => agreement.status === 'extended').length;
    },
    warningAgreements() {
      // For admin/seller, use server-fetched warnings when available (complete data, not limited by pagination)
      if (this.isAdminOrSeller && this.serverWarningAgreements) {
        return this.serverWarningAgreements;
      }

      // For non-admin users, calculate client-side (they have all agreements loaded)
      const results = [];

      this.agreements.forEach(agreement => {
        const warningKeys = [];
        const warningLabels = [];

        // Invoice sent but no PDF
        if (agreement.invoice_date != null && agreement.pdf_file == null) {
          warningKeys.push('invoice_without_pdf');
          warningLabels.push(this.$t('agreements.warnings.invoice_without_pdf'));
        }

        // No alarms
        const alarmCount = this.getAlarmCount(agreement.agreement_number);
        if (alarmCount === 0) {
          warningKeys.push('without_alarms');
          warningLabels.push(this.$t('agreements.warnings.without_alarms'));
        }

        // Extended agreements (status = extended)
        if (agreement.status === 'extended') {
          warningKeys.push('extended');
          warningLabels.push(this.$t('agreements.warnings.extended'));
        }

        // Dismissed past cancellation date
        if (agreement.dismissal_info && agreement.dismissal_info[0] && agreement.dismissal_info[0].dismissal_date) {
          const dismissalDate = this.moment(agreement.dismissal_info[0].dismissal_date);
          const now = this.moment();
          if (dismissalDate.isBefore(now, 'day')) {
            warningKeys.push('dismissed_past_cancellation_date');
            warningLabels.push(this.$t('agreements.warnings.dismissed_past_cancellation_date'));
          }
        }

        // Dismissed with devices
        if (agreement.status === 'dismissed' && alarmCount > 0 && agreement.dismissal_info && agreement.dismissal_info[0] && agreement.dismissal_info[0].dismissal_date) {
          const inOneMonth = this.moment().add(1, 'month');
          const dismissalDate = this.moment(agreement.dismissal_info[0].dismissal_date);
          if (dismissalDate.isBefore(inOneMonth)) {
            warningKeys.push('dismissed_with_devices');
            warningLabels.push(this.$t('agreements.warnings.dismissed_with_devices'));
          }
        }

        if (warningKeys.length > 0) {
          results.push({
            _agreement: agreement,
            agreement_number: agreement.agreement_number,
            customer_name: agreement.customer_name,
            status_label: this.getStatusLabel(agreement.status),
            warning_keys: warningKeys,
            warning_labels: warningLabels,
            warning_types_text: warningLabels.join(', '),
            warning_count: warningKeys.length,
            contract_start: this.formatOptionalDate(agreement.contract_start),
            contract_period: agreement.contract_period,
            seller_name: this.getSellerName(agreement.seller),
            rental_month_cost: agreement.rental_month_cost,
            alarm_count: alarmCount
          });
        }
      });

      return results;
    },
  },

  methods: {
    /**
     * Checks if the current user can perform actions (edit, menu, invoice) on this agreement.
     * Admin can act on any agreement. Sellers can only act on their own.
     */
    canActOnAgreement(agreement) {
      if (this.user.role === 'admin') return true;
      if (this.user.role === 'seller') return agreement.seller === this.user.seller_id;
      return false;
    },

    getAgreements() {
      let id = this.id;
      
      if ((this.user.role == "superuser" || this.user.role == "user") && id == "-") id = "";

      let type = this.type;
      if (this.user.role == "superuser") type = "customer";
      else if (this.user.role == "user") type = "unit";
      else if (this.user.role == "reseller") type = "reseller";
      else if (this.user.role == "seller") type = "seller";

      let apiUrl = `${process.env.VUE_APP_SERVER_URL}/agreement/${type}/${id}`;

      // Use server-side pagination for admin and seller
      if (this.isAdminOrSeller){
        apiUrl = `${process.env.VUE_APP_SERVER_URL}/agreements/?page=${this.currentPage}&limit=${this.pageSize}`;
        
        // Add search query if exists
        if (this.searchQuery && this.searchQuery.trim()) {
          apiUrl += `&search=${encodeURIComponent(this.searchQuery)}`;
        }
        
        // Add status filter (support grouped statuses)
        if (this.activeStatusFilter) {
          // activeStatusFilter takes priority when set from status buttons
          if (this.activeStatusFilter === 'active') {
            // Active button should include active, extended, and redrawn
            apiUrl += `&status=active,extended,redrawn`;
          } else {
            apiUrl += `&status=${this.activeStatusFilter}`;
          }
        } else if (this.statusFilter && this.statusFilter !== 'all') {
          // Fallback to regular statusFilter
          apiUrl += `&status=${this.statusFilter}`;
        }
        
        // Add expiring filter (time-based filtering)
        if (this.activeFilter) {
          apiUrl += `&expiringInMonths=${this.activeFilter}`;
        }

        if (this.shortTermFilter) {
          apiUrl += `&shortTerm=true`;
        }

        if (this.advancedFilters.countryOrigin) {
          apiUrl += `&countryOrigin=${this.advancedFilters.countryOrigin}`;
        }

        // Active category filter (financed / PIF)
        if (this.activeCategoryFilter && this.activeCategoryFilter !== 'all') {
          apiUrl += `&category=${encodeURIComponent(this.activeCategoryFilter)}`;
        }

        // Advanced filters (server-side) - 'yes' = must have, 'no' = must NOT have
        if (this.advancedFilters.pdfOnly) {
          apiUrl += `&pdfOnly=${this.advancedFilters.pdfOnly}`;
        }
        if (this.advancedFilters.invoiceSent) {
          apiUrl += `&invoiceSent=${this.advancedFilters.invoiceSent}`;
        }
        if (this.advancedFilters.hasAlarms) {
          apiUrl += `&hasAlarms=${this.advancedFilters.hasAlarms}`;
        }
        if (this.advancedFilters.hasInsurance){
          apiUrl += `&hasInsurance=${this.advancedFilters.hasInsurance}`;
        }
        if (this.advancedFilters.selectedSeller) {
          apiUrl += `&seller=${encodeURIComponent(this.advancedFilters.selectedSeller)}`;
        } else if (this.advancedFilters.selectedReseller) {
          apiUrl += `&seller=${encodeURIComponent(this.advancedFilters.selectedReseller)}`;
        }
        if (this.advancedFilters.customerSearch && this.advancedFilters.customerSearch.trim()) {
          apiUrl += `&customerSearch=${encodeURIComponent(this.advancedFilters.customerSearch.trim())}`;
        }

        // Warning filters (server-side) - 'yes' = must have, 'no' = must NOT have
        if (this.advancedFilters.invoiceWithoutPdf) {
          apiUrl += `&invoiceWithoutPdf=${this.advancedFilters.invoiceWithoutPdf}`;
        }
        if (this.advancedFilters.withoutAlarms) {
          apiUrl += `&withoutAlarms=${this.advancedFilters.withoutAlarms}`;
        }
        if (this.advancedFilters.dismissedPastCancellation) {
          apiUrl += `&dismissedPastCancellation=${this.advancedFilters.dismissedPastCancellation}`;
        }
        if (this.advancedFilters.dismissedWithDevices) {
          apiUrl += `&dismissedWithDevices=${this.advancedFilters.dismissedWithDevices}`;
        }
      }
      else if (type != "customer" && type != "unit" && type != "reseller" && type != "seller") return;
        
        this.isLoading = true;
        this.axios
        .get(apiUrl)
        .then((response) => {
          this.agreements = response.data.agreements;
          if (this.user.role == "admin" || this.user.role == "seller") this.agreement_alarms = response.data.agreement_alarms;
          
          // Handle pagination response for admin
          if (response.data.pagination) {
            this.totalRecords = response.data.pagination.total;
            this.totalPages = response.data.pagination.totalPages;
            this.currentPage = response.data.pagination.page;
          }
          
          // Store server-calculated stats (from all agreements, not just paginated)
          if (response.data.stats) {
            this.serverStats = response.data.stats;
          }
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getAlarmCenters() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/alarm-central`)
        .then((response) => {
          this.alarm_centers = response.data.data;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    getSellerReseller() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/seller/reseller`)
        .then((response) => {
          this.sellers = response.data.data;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    sendInvoiceEmailPrompt(agreement) {
      this.$modal.show("dialog", {
        title: `<span class="text-sm font-semibold">${this.$t('agreements.confirm_send_invoice')} <b>${agreement.agreement_number}</b></span>`,
        buttons: [
          {
            title: `<div class="bg-secondary-500 text-white text-sm font-sans py-2">${this.$t("no").toUpperCase()}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t("yes").toUpperCase()}</div>`,
            handler: () => {
              this.sendInvoiceEmail(agreement);
            },
          },
        ],
      });
    },

    async sendInvoiceEmail(agreement) {
      try {
        this.isLoading = true;

        let headers = {
          "Content-Type": "multipart/form-data",
          "X-Access-Token": this.$store.state.token,
        };

        let pdfFile = null;

        if (agreement.pdf_file == null) {
          let doc = this.generateAgreementPDF(agreement);
          pdfFile = doc.output("blob");
        } else {
          let res = await fetch(agreement.pdf_file);

          if (res.ok) {
            pdfFile = await res.blob();
          } else {
            this.handleError(null, this.$t('agreements.pdf_error'));
            this.isLoading = false;
            return;
          }
        }

        let formData = new FormData();
        formData.append("pdf", pdfFile);

        // eslint-disable-next-line
        let response = await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/agreement/invoice/${agreement._id}`, formData, headers);

        this.getAgreements();
        this.$modal.hide("dialog");
        this.isLoading = false;
        this.handleSuccess(this.$t('agreements.invoice_sent_success'));
      } catch (error) {
        this.handleError(error);
        this.$modal.hide("dialog");
        this.isLoading = false;
      }
    },

    async exportXLS() {
      let agreementsToExport = this.agreements;
      // Alarm/device counts keyed by agreement_number. For admin/seller this is
      // populated from the export response so counts are correct for every row
      // (not just the current page). Falls back to the page-level map otherwise.
      let exportAlarmCounts = null;

      // For admin/seller, fetch ALL agreements matching current filters (bypass pagination)
      if (this.isAdminOrSeller) {
        try {
          this.isLoading = true;
          let apiUrl = `${process.env.VUE_APP_SERVER_URL}/agreements/?page=1&limit=999999&export=true`;

          if (this.searchQuery && this.searchQuery.trim()) {
            apiUrl += `&search=${encodeURIComponent(this.searchQuery)}`;
          }
          if (this.activeStatusFilter) {
            if (this.activeStatusFilter === 'active') {
              apiUrl += `&status=active,extended,redrawn`;
            } else {
              apiUrl += `&status=${this.activeStatusFilter}`;
            }
          } else if (this.statusFilter && this.statusFilter !== 'all') {
            apiUrl += `&status=${this.statusFilter}`;
          }
          if (this.activeFilter) {
            apiUrl += `&expiringInMonths=${this.activeFilter}`;
          }
          if (this.shortTermFilter) {
            apiUrl += `&shortTerm=true`;
          }
          if (this.advancedFilters.countryOrigin) {
            apiUrl += `&countryOrigin=${this.advancedFilters.countryOrigin}`;
          }
          if (this.activeCategoryFilter && this.activeCategoryFilter !== 'all') {
            apiUrl += `&category=${encodeURIComponent(this.activeCategoryFilter)}`;
          }
          if (this.advancedFilters.pdfOnly) {
            apiUrl += `&pdfOnly=${this.advancedFilters.pdfOnly}`;
          }
          if (this.advancedFilters.invoiceSent) {
            apiUrl += `&invoiceSent=${this.advancedFilters.invoiceSent}`;
          }
          if (this.advancedFilters.hasAlarms) {
            apiUrl += `&hasAlarms=${this.advancedFilters.hasAlarms}`;
          }
          if (this.advancedFilters.hasInsurance) {
            apiUrl += `&hasInsurance=${this.advancedFilters.hasInsurance}`;
          }
          if (this.advancedFilters.selectedSeller) {
            apiUrl += `&seller=${encodeURIComponent(this.advancedFilters.selectedSeller)}`;
          } else if (this.advancedFilters.selectedReseller) {
            apiUrl += `&seller=${encodeURIComponent(this.advancedFilters.selectedReseller)}`;
          }
          if (this.advancedFilters.customerSearch && this.advancedFilters.customerSearch.trim()) {
            apiUrl += `&customerSearch=${encodeURIComponent(this.advancedFilters.customerSearch.trim())}`;
          }
          if (this.advancedFilters.invoiceWithoutPdf) {
            apiUrl += `&invoiceWithoutPdf=${this.advancedFilters.invoiceWithoutPdf}`;
          }
          if (this.advancedFilters.withoutAlarms) {
            apiUrl += `&withoutAlarms=${this.advancedFilters.withoutAlarms}`;
          }
          if (this.advancedFilters.dismissedPastCancellation) {
            apiUrl += `&dismissedPastCancellation=${this.advancedFilters.dismissedPastCancellation}`;
          }
          if (this.advancedFilters.dismissedWithDevices) {
            apiUrl += `&dismissedWithDevices=${this.advancedFilters.dismissedWithDevices}`;
          }

          const response = await this.axios.get(apiUrl);
          agreementsToExport = response.data.agreements;
          // Use the server-aggregated alarm counts for ALL exported agreements.
          // (this.alarmCountMap only covers the currently displayed page.)
          exportAlarmCounts = response.data.alarm_counts || {};
        } catch (error) {
          this.handleError(error);
          this.isLoading = false;
          return;
        } finally {
          this.isLoading = false;
        }
      }

      let export_columns = [];

      agreementsToExport.forEach((agreement) => {
        // Calculate remaining contract time
        const remainingTime = this.calculateRemainingTime(agreement);
        
        // Get alarm count for this agreement
        const alarmCount = exportAlarmCounts
          ? (exportAlarmCounts[agreement.agreement_number] || 0)
          : this.getAlarmCount(agreement.agreement_number);
        
        // Get alarm center name
        const alarmCenterName = this.getAlarmCenterName(agreement.alarm_center);
        
        export_columns.push({
          [this.$t("export_agreement.agreement_number")]: agreement.agreement_number,
          [this.$t("export_agreement.status")]: this.getStatusLabel(agreement.status),
          [this.$t("export_agreement.agreement_category")]: this.getCategoryLabel(agreement.agreement_category),
          [this.$t("export_agreement.contract_period")]: agreement.contract_period,
          [this.$t("export_agreement.contract_start")]: agreement.contract_start ? this.formatDate(agreement.contract_start) : '',
          [this.$t("export_agreement.contract_end")]: this.calculateExpiryDateString(agreement),
          [this.$t("export_agreement.remaining_time")]: remainingTime,
          [this.$t("export_agreement.name")]: agreement.customer_name,
          [this.$t("export_agreement.company_number")]: agreement.company_number || '',
          [this.$t("export_agreement.customer_phone")]: agreement.customer_phone || '',
          [this.$t("export_agreement.contact_person")]: agreement.customer_contact,
          [this.$t("export_agreement.your_reference")]: agreement.customer_reference,
          [this.$t("export_agreement.billing_address")]: agreement.billing_adress || '',
          [this.$t("export_agreement.billing_zip_code")]: agreement.billing_zip_code || '',
          [this.$t("export_agreement.billing_city")]: agreement.billing_city || '',
          [this.$t("export_agreement.e_bill")]: agreement.e_bill || '',
          [this.$t("export_agreement.seller")]: this.getSellerName(agreement.seller),
          [this.$t("export_agreement.rental_month_cost")]: this.formatAmount(agreement.rental_month_cost) || '',
          [this.$t("export_agreement.setup_cost")]: this.formatAmount(agreement.setup_cost) || '',
          [this.$t("export_agreement.cash_sum")]: this.formatAmount(agreement.cash_sum) || '',
          [this.$t("export_agreement.insurance")]: agreement.insurance ? this.$t("export_agreement.yes") : this.$t("export_agreement.no"),
          [this.$t("export_agreement.alarm_center")]: alarmCenterName,
          [this.$t("export_agreement.direct_dispatch")]: agreement.direct_dispatch ? this.$t("export_agreement.yes") : this.$t("export_agreement.no"),
          [this.$t("export_agreement.sim_card")]: agreement.sim_card || '',
          [this.$t("export_agreement.alarm_count")]: alarmCount,
          [this.$t("export_agreement.units_specification")]: agreement.units_specification || '',
          [this.$t("export_agreement.payment_terms")]: agreement.payment_terms || '',
          [this.$t("export_agreement.special_terms")]: agreement.special_terms || '',
          [this.$t("export_agreement.invoice_date")]: agreement.invoice_date ? this.formatDate(agreement.invoice_date) : '',
          [this.$t("export_agreement.pdf_uploaded")]: agreement.pdf_file ? this.$t("export_agreement.yes") : this.$t("export_agreement.no"),
          [this.$t("export_agreement.extended_count")]: agreement.extendedCount || 0,
          [this.$t("export_agreement.original_agreement")]: agreement.original_agreement_number || '',
          [this.$t("export_agreement.created_at")]: agreement.createdAt ? this.formatDate(agreement.createdAt) : '',
        });
      });

      this.excelExport(export_columns, this.$t("export_agreement_sheet.sheet_name"), this.$t("export_agreement_sheet.file_name"));
    },
    async deleteAgreement(_id) {
      try {
        // eslint-disable-next-line
        let response = await this.axios.delete(`${process.env.VUE_APP_SERVER_URL}/agreements/${_id}`);

        this.$modal.hide("dialog");
        this.handleSuccess(this.$t('modals.agreement_deleted'));

        this.getAgreements();
      } catch (error) {
        this.$modal.hide("dialog");
        this.handleError(error);
      }
    },

    getSellerName(id) {
      if (!this.sellers || !this.sellers.length) return "";
      let seller = _.find(this.sellers, { _id: id });
      if (seller) return seller.name;
      else return "";
    },
    formatSellerDisplay(name) {
      if (!name || name.length <= 20) return name;
      const spaceIndex = name.indexOf(' ');
      if (spaceIndex > 0) return name.substring(0, spaceIndex) + '\n' + name.substring(spaceIndex + 1);
      return name;
    },
    calculateExpiryDate(agreement) {
      if (!agreement.contract_start || !agreement.contract_period) {
        return null;
      }
      
      const periodMatch = agreement.contract_period.match(/(\d+)/);
      if (!periodMatch) return null;
      
      const months = parseInt(periodMatch[1]);
      const startDate = this.moment(agreement.contract_start);
      
      return startDate.add(months, 'months');
    },

    calculateExpiryDateString(agreement) {
      const expiryDate = this.calculateExpiryDate(agreement);
      if (!expiryDate) return '';
      return expiryDate.format('YYYY-MM-DD');
    },

    calculateRemainingTime(agreement) {
      if (!agreement.contract_start || !agreement.contract_period) {
        return '';
      }
      
      const periodMatch = agreement.contract_period.match(/(\d+)/);
      if (!periodMatch) return '';
      
      const months = parseInt(periodMatch[1]);
      const startDate = this.moment(agreement.contract_start);
      const expiryDate = startDate.clone().add(months, 'months');
      const now = this.moment();
      
      if (expiryDate.isBefore(now)) {
        const monthsAgo = now.diff(expiryDate, 'months');
        return `${this.$t('agreements.expired')} (${monthsAgo} ${this.$t('agreements.months_ago')})`;
      }
      
      const remainingMonths = expiryDate.diff(now, 'months');
      const remainingDays = expiryDate.diff(now, 'days') % 30;
      
      if (remainingMonths > 0) {
        return `${remainingMonths} mån${remainingDays > 0 ? `, ${remainingDays} dagar` : ''}`;
      }
      return `${remainingDays} dagar`;
    },

    getCategoryLabel(category) {
      const categoryMap = {
        'rental': this.$t('export_agreement.category_rental'),
        'cash': this.$t('export_agreement.category_cash'),
        'services_software': this.$t('export_agreement.category_services_software'),
        'cash_services': this.$t('export_agreement.category_cash_services'),
        'financed': this.$t('export_agreement.category_financed'),
        'financed_umea': this.$t('admin_order_alarm.category_financed_umea'),
        'financed_ikano': this.$t('admin_order_alarm.category_financed_ikano')
      };
      return categoryMap[category] || category || '';
    },

    getAlarmCenterName(alarmCenterId) {
      if (!alarmCenterId || !this.alarm_centers) return '';
      const alarmCenter = this.alarm_centers.find(ac => ac._id === alarmCenterId);
      return alarmCenter ? alarmCenter.name : '';
    },

    getExpiringCount(months) {
      // Use server stats for admin/seller (calculated on all agreements)
      if (this.isAdminOrSeller && this.serverStats) {
        switch(months) {
          case 6: return this.serverStats.expiringCounts.months6;
          case 12: return this.serverStats.expiringCounts.months12;
          case 24: return this.serverStats.expiringCounts.months24;
        }
      }
      
      // Fallback: calculate from loaded agreements (for other roles or when stats not available)
      const now = this.moment();
      const filterDate = this.moment().add(months, 'months');
      
      const count = this.agreements.filter(agreement => {
        // Only count active-type agreements for expiry counts
        if (!['active', 'extended', 'redrawn'].includes(agreement.status)) {
          return false;
        }
        
        // Exclude cash agreements (contract_period = 0)
        if (agreement.contract_period === 0 || agreement.contract_period === '0') {
          return false;
        }
        
        const expiryDate = this.calculateExpiryDate(agreement);
        if (!expiryDate) return false;
        
        return expiryDate.isAfter(now) && expiryDate.isSameOrBefore(filterDate);
      }).length;
      
      return count;
    },

    getActiveCount() {
      // Use server stats for admin/seller (calculated on all agreements)
      if (this.isAdminOrSeller && this.serverStats) {
        if (this.activeCategoryFilter === 'financed') {
          return this.serverStats.activeCounts.financed;
        } else if (this.activeCategoryFilter === 'pif') {
          return this.serverStats.activeCounts.pif;
        }
        return this.serverStats.activeCounts.total;
      }
      
      // Fallback: calculate from loaded agreements (for other roles or when stats not available)
      let activeAgreements = this.agreements.filter(agreement => 
        agreement.status === 'active' || agreement.status === 'extended' || agreement.status === 'redrawn'
      );

      if (this.activeCategoryFilter && this.activeCategoryFilter !== 'all') {
        activeAgreements = activeAgreements.filter(agreement => this.matchesActiveCategory(agreement, this.activeCategoryFilter));
      }

      return activeAgreements.length;
    },

    async toggleFilter(months) {
      // Toggle filter - if clicking the same button, turn off filter
      if (this.activeFilter === months) {
        this.activeFilter = null;
      } else {
        this.activeFilter = months;
      }
      
      // Reset to first page and fetch with server-side filtering
      if (this.isAdminOrSeller) {
        this.currentPage = 1;
        this.getAgreements();
      }
    },

    async toggleStatus(status) {
      // Toggle status filter - if clicking the same button, turn off filter
      if (this.activeStatusFilter === status) {
        this.activeStatusFilter = null;
      } else {
        this.activeStatusFilter = status;
      }
      
      // Reset to first page and fetch with server-side filtering
      if (this.isAdminOrSeller) {
        this.currentPage = 1;
        this.getAgreements();
      }
    },

    toggleShortTerm() {
      this.shortTermFilter = !this.shortTermFilter;
      if (this.isAdminOrSeller) {
        this.currentPage = 1;
        this.getAgreements();
      }
    },

    getShortTermCount() {
      if (this.isAdminOrSeller && this.serverStats) {
        return this.serverStats.shortTermCount || 0;
      }
      return this.agreements.filter(agreement => {
        const months = parseInt(agreement.contract_period, 10);
        return months >= 1 && months <= 6;
      }).length;
    },

    toggleMyAgreements() {
      if (this.isMyAgreementsActive) {
        this.advancedFilters.selectedSeller = null;
      } else if (this.mySellerRecord) {
        this.advancedFilters.selectedSeller = this.mySellerRecord._id;
      }
      this.currentPage = 1;
      this.getAgreements();
    },

    getStatusCount(status) {
      // Use server stats for admin/seller (calculated on all agreements)
      if (this.isAdminOrSeller && this.serverStats) {
        const counts = this.serverStats.statusCounts;
        if (status === 'active') {
          // Active button includes active + extended + redrawn
          return counts.active + counts.extended + counts.redrawn;
        }
        return counts[status] || 0;
      }
      
      // Fallback: calculate from loaded agreements (for other roles or when stats not available)
      if (status === 'active') {
        return this.agreements.filter(agreement =>
          agreement.status === 'active' || agreement.status === 'extended' || agreement.status === 'redrawn'
        ).length;
      }
      if (status === 'no_status') {
        return this.agreements.filter(agreement => !agreement.status).length;
      }
      return this.agreements.filter(agreement => agreement.status === status).length;
    },

    getDraftCount() {
      if (this.isAdminOrSeller && this.serverStats) {
        const counts = this.serverStats.statusCounts;
        return (counts.temporary || 0) + (counts.deleted_draft || 0);
      }
      return this.agreements.filter(a => a.status === 'temporary' || a.status === 'deleted_draft').length;
    },

    getTotalCount() {
      // Sum all the individual status counts that are displayed
      // Note: 'active' button already includes extended and redrawn in its count,
      // so we don't add them separately to avoid double-counting
      return (
        this.getStatusCount('pending') +
        this.getStatusCount('active') + // Already includes extended + redrawn
        this.getStatusCount('dismissed') +
        this.getStatusCount('returned') +
        this.getStatusCount('active_old') +
        this.getStatusCount('no_status')
      );
    },

    getAlarmCount(agreement_number) {
      return this.alarmCountMap[agreement_number] || 0;
    },

    formatDate(date) {
      return this.moment(date).format("YYYY-MM-DD");
    },

    formatOptionalDate(date) {
      if (!date) return '';
      return this.formatDate(date);
    },

    formatAmountField(value) {
      return this.formatAmount(value);
    },

    showEdit(agreement) {
      this.updateInfo = _.cloneDeep(agreement);
      this.$modal.show("update-agreement");
    },

    showAgreementAlarm(agreementNumber) {
      this.selectedAgreementNumber = agreementNumber;
      this.$modal.show("agreement-alarm");
    },

    showFilterModal() {
      this.$modal.show("agreement-filter");
    },

    async showWarningsModal() {
      // For admin/seller, fetch complete warning data from server (not limited by pagination)
      if (this.isAdminOrSeller) {
        try {
          this.isLoading = true;
          const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/agreements/warnings`);

          // Transform server response to match the format the modal expects
          this.serverWarningAgreements = response.data.warnings.map(warning => {
            const warningLabels = warning.warning_keys.map(key => this.$t(`agreements.warnings.filter_short.${key}`));
            return {
              _agreement: warning,
              agreement_number: warning.agreement_number,
              customer_name: warning.customer_name,
              status_label: this.getStatusLabel(warning.status),
              warning_keys: warning.warning_keys,
              warning_labels: warningLabels,
              warning_types_text: warningLabels.join(', '),
              warning_count: warning.warning_keys.length,
              contract_start: this.formatOptionalDate(warning.contract_start),
              contract_period: warning.contract_period,
              seller_name: this.getSellerName(warning.seller),
              rental_month_cost: warning.rental_month_cost,
              alarm_count: warning.alarm_count
            };
          });
        } catch (error) {
          this.handleError(error);
        } finally {
          this.isLoading = false;
        }
      }

      this.$modal.show("agreements-warnings");
    },

    matchesActiveCategory(agreement, category) {
      if (!agreement) return false;
      if (category === 'financed') {
        return agreement.agreement_category === 'financed';
      }
      if (category === 'pif') {
        return typeof agreement.agreement_number === 'string' && agreement.agreement_number.endsWith('PIF');
      }
      return true;
    },

    openMenuFromWarnings(agreement, event) {
      if (!agreement) return;
      this.openMenuId = agreement.agreement_number;
      this.selectedAgreementForMenu = agreement;

      this.$nextTick(() => {
        const button = event?.target?.closest('button');
        if (button) {
          const rect = button.getBoundingClientRect();
          this.positionMenuNearButton(rect);
        }
      });
    },

    async showMultipleAgreementModal() {
      try {
        // Fetch ALL agreements with minimal fields for performance
        // Only fetch the fields needed for the bulk modal
        const apiUrl = `${process.env.VUE_APP_SERVER_URL}/agreements/?limit=999999&minimal=true`;
        const response = await this.axios.get(apiUrl);
        
        // Pass all agreements, alarm data, and pre-selected IDs to the modal
        this.$modal.show("agreement-mark-multiple", {
          allAgreements: response.data.agreements,
          agreementAlarms: this.agreement_alarms || [],
          preSelectedIds: [...this.selectedAgreementIds]
        });
      } catch (error) {
        this.handleError(error);
      }
    },

    handleFilterChange(filters) {
      this.advancedFilters = { ...filters };
      this.statusFilter = this.advancedFilters.status || 'all';

      if (this.isAdminOrSeller) {
        this.currentPage = 1;
        this.getAgreements();
      }
    },

    toggleAgreementSelection(agreementId) {
      const index = this.selectedAgreementIds.indexOf(agreementId);
      if (index === -1) {
        this.selectedAgreementIds.push(agreementId);
      } else {
        this.selectedAgreementIds.splice(index, 1);
      }
    },

    toggleMenu(agreementNumber, event) {
      if (this.openMenuId === agreementNumber) {
        this.openMenuId = null;
        this.selectedAgreementForMenu = null;
      } else {
        this.openMenuId = agreementNumber;
        
        // Find the agreement object
        this.selectedAgreementForMenu = this.agreements.find(a => a.agreement_number === agreementNumber);
        
        // Calculate menu position based on button position
        this.$nextTick(() => {
          const button = event.target.closest('button');
          if (button) {
            const rect = button.getBoundingClientRect();
            this.positionMenuNearButton(rect);
          }
        });
      }
    },
    
    async createNewExtendedAgreements(extendedAgreements) {
        try{
          let response = await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/agreements/create-extended`, {
            agreements: extendedAgreements
          });
          await this.getAgreements();
          
          return response.data;
        } catch (error) {
          console.error('Failed to create extended agreements:', error);
        }      
    },


    positionMenuNearButton(rect) {
      const menuWidth = 224; // w-56 = 14rem
      const padding = 8;
      const left = Math.max(padding, rect.right - menuWidth);

      this.menuPosition = {
        top: `${rect.bottom + padding}px`,
        left: `${left}px`
      };

      // After the menu renders, check if it overflows the viewport bottom and flip upward
      this.$nextTick(() => {
        const menu = this.$refs.agreementsDropdown;
        if (menu) {
          const menuRect = menu.getBoundingClientRect();
          if (menuRect.bottom > window.innerHeight - padding) {
            const flippedTop = rect.top - menuRect.height - padding;
            this.menuPosition = {
              top: `${Math.max(padding, flippedTop)}px`,
              left: `${left}px`
            };
          }
        }
      });
    },

    closeMenu() {
      this.openMenuId = null;
      this.selectedAgreementForMenu = null;
    },

    handleMenuAction(action, agreement) {
      this.openMenuId = null; // Close menu
      
      switch(action) {
        case 'terminate':
          this.selectedAgreementForDismissal = agreement;
          this.$modal.show('dismiss-agreement', { agreement: this.selectedAgreementForDismissal });
          break;
        case 'cancel':
          this.selectedAgreementForCancellation = agreement;
          this.$modal.show('cancel-agreement', { agreement: this.selectedAgreementForCancellation });
          break;
        case 'report_damage':
          this.selectedAgreementForDamage = agreement;
          this.$modal.show('report-damage', { agreement: this.selectedAgreementForDamage });
          break;
        case 'add_insurance':
          this.confirmAddInsurance(agreement);
          break;
        case 'remove_insurance':
          //Dont know if we need to store the selectedAgreementForInsuranceRemoval and forDamge, but for consistency i did it.
          // We could pass the agreement directly to the modal. 
          this.selectedAgreementForInsuranceRemoval = agreement;
          this.$modal.show('remove-insurance', { agreement: this.selectedAgreementForInsuranceRemoval });
          break;
        case 'end_now':
          this.selectedAgreementForEndNow = agreement;
          this.$modal.show('end-agreement', { agreement: this.selectedAgreementForEndNow });
          break;
        case 'redraw':
          this.selectedAgreementForRedraw = agreement;
          this.$modal.show('redraw-agreement', { agreement: this.selectedAgreementForRedraw });
          break;
        case 'reActivate':
          this.confirmReactivation(agreement);
          break;
        case 'note_commission':
          this.selectedAgreementForCommission = agreement;
          this.noteCommision(this.selectedAgreementForCommission);
          break;
        case 'undo_invoice':
          this.confirmUndoInvoice(agreement);
          break;
        case 'activate_draft':
          this.$modal.show('activate-temporary-agreement', { agreement: agreement });
          break;
        case 'delete_draft':
          this.confirmDeleteDraft(agreement);
          break;
      }
    },

    confirmUndoInvoice(agreement) {
      this.$modal.show('dialog', {
        title: `<span class="text-sm font-semibold">${this.$t('agreements.confirm_undo_invoice')} - ${agreement.agreement_number}</span>`,
        buttons: [
          {
            title: this.$t('modals.cancel'),
            handler: () => this.$modal.hide('dialog')
          },
          {
            title: this.$t('agreements.menu.undo_invoice'),
            class: 'btn-danger',
            handler: () => {
              this.$modal.hide('dialog');
              this.undoInvoice(agreement);
            }
          }
        ]
      });
    },

    async undoInvoice(agreement) {
      try {
        this.isLoading = true;
        await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/agreement/undo-invoice/${agreement._id}`);
        this.handleSuccess(this.$t('agreements.undo_invoice_success'));
        this.getAgreements();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    confirmDeleteDraft(agreement) {
      this.$modal.show('dialog', {
        title: `<span class="text-sm font-semibold">${this.$t('agreements.confirm_delete_draft')} - ${agreement.agreement_number}</span>`,
        buttons: [
          {
            title: this.$t('modals.cancel'),
            handler: () => this.$modal.hide('dialog')
          },
          {
            title: this.$t('agreements.menu.delete_draft'),
            class: 'btn-danger',
            handler: () => {
              this.$modal.hide('dialog');
              this.softDeleteDraft(agreement);
            }
          }
        ]
      });
    },

    async softDeleteDraft(agreement) {
      try {
        this.isLoading = true;
        await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/agreement/soft-delete/${agreement._id}`);
        this.handleSuccess(this.$t('agreements.delete_draft_success'));
        this.getAgreements();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    confirmAddInsurance(agreement) {
      
      this.$modal.show("dialog", {
        title: `<span class="text-sm font-semibold">${this.$t('agreements.confirm_add_insurance')}</span>`,
        buttons: [
          {
            title: `<div class="bg-gray-500 text-white text-sm font-sans py-2">${this.$t("user_actions.cancel")}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: `<div class="bg-green-500 text-white text-sm font-sans py-2">${this.$t("user_actions.confirm")}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
              this.addInsuranceToAgreement(agreement);
            },
          },
        ],
      });
    },
    confirmReactivation(agreement) {
      this.$modal.show("dialog", {
        title: `<span class="text-sm font-semibold">${this.$t('agreements.confirm_reactivation')} - ${agreement.agreement_number}</span>`,
        buttons: [
          {
            title: `<div class="bg-gray-500 text-white text-sm font-sans py-2">${this.$t("user_actions.cancel")}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: `<div class="bg-green-500 text-white text-sm font-sans py-2">${this.$t("user_actions.confirm")}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
              this.reactivateAgreement(agreement);
            },
          },
        ],
      });
    },

    noteCommision(agreement){
      this.$modal.show("note-commission", {
        agreement: agreement
      });
    },

    async addInsuranceToAgreement(agreement) {
      try {
        await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/agreement/add-insurance/${agreement._id}`);
        this.handleSuccess(this.$t('agreements.add_insurance_success'));
        this.getAgreements();
      } catch (error) {
        this.handleError(error);
      }
    },
    async reactivateAgreement(agreement) {
      try {
        await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/agreement/reactivate/${agreement._id}`);
        this.handleSuccess(this.$t('agreements.reactivation_success'));
        this.getAgreements();
      } catch (error) {
        this.handleError(error);
      }
    },

    toggleChildAgreements(agreementNumber) {
      const index = this.expandedParents.indexOf(agreementNumber);
      if (index > -1) {
        this.expandedParents.splice(index, 1);
      } else {
        this.expandedParents.push(agreementNumber);
      }
    },

    // Pagination methods for Vue Good Table
    onPageChange(params) {
      if (this.isAdminOrSeller && !this.useClientSideFiltering) {
        this.currentPage = params.currentPage;
        this.getAgreements();
      }
    },

    onPerPageChange(params) {
      if (this.isAdminOrSeller && !this.useClientSideFiltering) {
        this.pageSize = params.currentPerPage;
        this.currentPage = 1; // Reset to first page when changing page size
        this.getAgreements();
      }
    },

    onSearch(params) {
      if (this.isAdminOrSeller && !this.useClientSideFiltering) {
        // Debounce search to avoid too many API calls
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.searchQuery = (params.searchTerm || '').trim();
          this.currentPage = 1; // Reset to first page when searching
          this.getAgreements();
        }, 500);
      }
    },

    getRowStyleClass(row) {
      let classes = 'text-sm';
      if (row._isChildRow) {
        classes += ' bg-blue-50';
      }
      return classes;
    },

    getStatusLabel(status) {
      // Legacy agreements never stored a status — say so rather than implying pending
      if (!status) return this.$t('agreements.no_status');
      const statusMap = {
        'active': this.$t('agreements.active'),
        'returned': this.$t('agreements.returned'),
        'dismissed': this.$t('agreements.dismissed'),
        'extended': this.$t('agreements.extended'),
        'pending': this.$t('agreements.pending'),
        'redrawn': this.$t('agreements.redrawn'),
        'active_old': this.$t('agreements.active_old'),
        'temporary': this.$t('agreements.temporary'),
        'deleted_draft': this.$t('agreements.deleted_draft')
      };
      return statusMap[status] || status || '-';
    },

    getStatusBadgeClass(status) {
      const baseClasses = 'inline-block px-2 py-1 text-xs font-semibold rounded';
      const statusClasses = {
        'active': 'text-green-700 bg-green-100',
        'extended': 'text-blue-700 bg-blue-100',
        'pending': 'text-yellow-700 bg-yellow-100',
        'dismissed': 'text-orange-700 bg-orange-100',
        'returned': 'text-gray-700 bg-gray-200',
        'redrawn': 'text-purple-700 bg-purple-100',
        'active_old': 'text-gray-400 bg-gray-100',
        'temporary': 'text-teal-700 bg-teal-100',
        'deleted_draft': 'text-red-700 bg-red-100'
      };
      return `${baseClasses} ${statusClasses[status] || 'text-gray-500 bg-gray-100'}`;
    },

    hasDismissalPdf(agreement) {
      if (agreement.dismissal_info && agreement.dismissal_info.length > 0) {
        return agreement.dismissal_info.some(d => d.dismissal_pdf);
      }
      if (agreement.cancellation_info && agreement.cancellation_info.length > 0) {
        return agreement.cancellation_info.some(c => c.cancellation_pdf);
      }
      return false;
    },

    openDismissalPdf(agreement) {
      if (agreement.dismissal_info && agreement.dismissal_info.length > 0) {
        const dismissal = agreement.dismissal_info.find(d => d.dismissal_pdf);
        if (dismissal) {
          window.open(dismissal.dismissal_pdf, '_blank');
          return;
        }
      }
      if (agreement.cancellation_info && agreement.cancellation_info.length > 0) {
        const cancellation = agreement.cancellation_info.find(c => c.cancellation_pdf);
        if (cancellation) {
          window.open(cancellation.cancellation_pdf, '_blank');
          return;
        }
      }
    },

    openInsurancePdf(agreement) {
      if (agreement.insurance_removal_pdf) {
        window.open(agreement.insurance_removal_pdf, '_blank');
      }
    },

    goToOriginalAgreement(originalAgreementNumber) {
      // Scroll to and highlight the original agreement
      const originalAgreement = this.agreements.find(a => a.agreement_number === originalAgreementNumber);
      if (originalAgreement) {
        this.showAgreementAlarm(originalAgreementNumber);
      }
    },

    handleClickOutside(event) {
      // Close menu if clicking outside and menu is open
      if (this.openMenuId !== null) {
        const dropdown = event.target.closest('.fixed.w-48');
        const button = event.target.closest('.btn-secondary-outline');
        
        if (!dropdown && !button) {
          this.closeMenu();
        }
      }
    },
  },

  created() {
    this.getAgreements();
    if (this.user.role == "admin" || this.user.role == "seller") this.getAlarmCenters();
    this.getSellerReseller();

    if (this.user.role == "admin") this.setPageTitle(this.$t("top_titles.agreement"), "agreements");
    else this.setPageTitle(this.$t("top_titles.agreement"), "customer_agreements");
  },

  mounted() {
    // Close menu when clicking outside
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('scroll', this.closeMenu, true); 
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('scroll', this.closeMenu, true);
  },

  watch: {
    "$i18n.locale"() {
      document.title = this.$t("page_titles.agreement");
      this.setPageTitle(this.$t("top_titles.agreement"), "customer_agreements");
    },
  },
};

</script>
